import React, { useEffect, useRef } from "react";
import PaymentDoneDynamic from "../../../components/paymentDone";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { useLocation, useNavigate } from "react-router";
import { sendTipApi, setPrevUrl } from "../../../store/slices/paymentSlices";
import {
  clearPendingTipPayment,
  getPendingTipPayment,
  getStripe,
  removeAllTokenAndSessionIds,
  showToast,
} from "../../../utils/functions";

const PaymentDone = () => {
  const { google_site_url, trip_advisor_url, uuid, yelp_url, facebook_url } = useSelector(
    (store: RootState) => store.paymentSlices.data
  );
  const { sessionId } = useSelector(
    (store: RootState) => store.sessionSlice
  );
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const hasFinalizedRedirectedPayment = useRef(false);
  const paymentIntentClientSecretFromUrl = new URLSearchParams(location.search).get(
    "payment_intent_client_secret"
  );

  useEffect(() => {
    const pendingTipPayment = getPendingTipPayment();

    if (!uuid && !paymentIntentClientSecretFromUrl && !pendingTipPayment) {
      removeAllTokenAndSessionIds()
      navigate(-2);
    }
  }, [navigate, paymentIntentClientSecretFromUrl, uuid]);

  useEffect(() => {
    const finalizeRedirectedTip = async () => {
      const pendingTipPayment = getPendingTipPayment();
      const paymentIntentClientSecret =
        paymentIntentClientSecretFromUrl || pendingTipPayment?.clientSecret;

      if (
        !paymentIntentClientSecret ||
        hasFinalizedRedirectedPayment.current
      ) {
        return;
      }

      if (!pendingTipPayment?.payload) {
        return;
      }

      hasFinalizedRedirectedPayment.current = true;

      const stripe = await getStripe();

      if (!stripe) {
        hasFinalizedRedirectedPayment.current = false;
        return;
      }

      const { paymentIntent, error } = await stripe.retrievePaymentIntent(
        paymentIntentClientSecret
      );

      if (error || paymentIntent?.status !== "succeeded") {
        hasFinalizedRedirectedPayment.current = false;
        return;
      }

      const confirmedPaymentMethod =
        paymentIntent.payment_method &&
          typeof paymentIntent.payment_method === "object"
          ? paymentIntent.payment_method
          : null;
      const confirmedCustomerName =
        pendingTipPayment.payload.customer_name ||
        confirmedPaymentMethod?.billing_details?.name?.trim() ||
        paymentIntent.shipping?.name?.trim() ||
        "";
      const payload = {
        ...pendingTipPayment.payload,
        stripe_transaction_id: paymentIntent.id,
        email:
          pendingTipPayment.payload.email ||
          confirmedPaymentMethod?.billing_details?.email?.trim() ||
          paymentIntent.receipt_email?.trim() ||
          null,
      };

      if (confirmedCustomerName) {
        payload.customer_name = confirmedCustomerName;
      } else {
        delete payload.customer_name;
      }

      const sendTipResponse = await dispatch(
        sendTipApi({
          data: payload,
          sessionId: pendingTipPayment.sessionId || sessionId,
        })
      ).unwrap();

      if (sendTipResponse?.status !== 1) {
        throw new Error(
          sendTipResponse?.message || "Payment completed, but the tip could not be saved."
        );
      }

      clearPendingTipPayment();
      if (paymentIntentClientSecretFromUrl) {
        navigate("/payment-completed", { replace: true });
      }
    };

    finalizeRedirectedTip().catch((error: any) => {
      hasFinalizedRedirectedPayment.current = false;
      showToast(
        error?.message ||
        error?.error ||
        "Payment completed, but the tip could not be saved.",
        "error"
      );
    });
  }, [dispatch, navigate, paymentIntentClientSecretFromUrl, sessionId]);

  useEffect(() => {
    dispatch(setPrevUrl(location.pathname));
  }, [location, dispatch]);

  return (
    <>
      <PaymentDoneDynamic
        isGoogleReview={!!google_site_url}
        isTripAdvisor={!!trip_advisor_url}
        is_yelp={!!yelp_url}
        is_facebook={!!facebook_url}
      />
    </>
  );
};

export default PaymentDone;
